import { useWorkspaceText } from '../interface-copy';
import { useCallback, useState } from 'react';
import { downloadDeck } from '../api';
import { useWorkspace } from '../context';
import { WorkspaceIcon } from '../icons';
import type { CompanyResult, DeckPayload } from '../types';
import { DeckStage } from './stage';
import { filmstripTarget } from './filmstrip';
import { Presenter } from './present';

// The deck in the canvas: one slide at a time on the stage, the whole deck in
// the filmstrip, and the current slide's speaker notes underneath, where they
// belong to the person about to present rather than to the slide.
//
// A .pptx is offered here and nowhere else. It is deliberately the reader's
// action, not something the model produces: the saved deck is already
// presentable, and generating a file nobody asked for is the behaviour this
// feature is meant to replace.
export function DeckView({ result, deck }: { result: CompanyResult; deck: DeckPayload }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const ws = useWorkspace();
  const [at, setAt] = useState(0);
  const [presenting, setPresenting] = useState(false);
  const [busy, setBusy] = useState(false);
  const count = deck.slides.length;
  // `at` can outrun the deck: a revision through `replaces` may shorten it
  // while this canvas tab is open.
  const shown = Math.min(at, count - 1);
  const current = deck.slides[shown];
  // Clamps both ends *and* its starting point. Only clamping the result is
  // enough for a ±1 step, but Home and End are absolute jumps expressed as an
  // overshoot — and `at` can be past the end, three lines up. From at=13 in a
  // 5-slide deck, `-count` lands on 8, which clamps to the last slide instead
  // of the first.
  const step = useCallback(
    (by: number) => setAt((was) => Math.min(count - 1, Math.max(0, Math.min(was, count - 1) + by))),
    [count],
  );
  const stopPresenting = useCallback(() => setPresenting(false), []);
  const download = async () => {
    setBusy(true);
    try {
      await downloadDeck(result);
    } catch (error) {
      ws.notify(error instanceof Error ? error.message : uiText('We could not build the file.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ws-deck">
      <div className="ws-deck-bar">
        <button className="ws-button" onClick={() => setPresenting(true)}>
          {uiRich('{value1} Present', { value1: <WorkspaceIcon name="play" size={14} /> })}
        </button>
        {/* Full view is the canvas header's expand control, in place; Copy link (⋯) shares this deck. */}
        <button className="ws-button" disabled={busy} onClick={() => void download()}>
          <WorkspaceIcon name="download" size={15} />
          {busy ? uiText('Building…') : uiText('Download PowerPoint')}
        </button>
        <span className="position">
          {uiRich('Slide {value1} of {value2}', { value1: shown + 1, value2: count })}
        </span>
      </div>
      <div id="deck-stage-panel" role="tabpanel" aria-labelledby={`deck-slide-${shown}`}>
        <DeckStage
          slide={current}
          label={uiText('Slide {value1} of {value2}', { value1: shown + 1, value2: count })}
        />
      </div>
      {/* A tablist owns its own arrow keys. Without this the roving tabindex
          below takes every unselected thumbnail out of the tab order and gives
          nothing back, which is worse than leaving them all tabbable. */}
      <div
        className="ws-filmstrip"
        role="tablist"
        aria-label={uiText('Slides')}
        onKeyDown={(event) => {
          // While the presenter is open it owns the arrow keys. It listens on
          // `window` and traps nothing, so focus can still be sitting on a
          // thumbnail behind the overlay — without this both handlers fire and
          // one press advances two slides.
          if (presenting) return;
          const to = filmstripTarget(event.key, shown, count);
          if (to === null) return;
          event.preventDefault();
          setAt(to);
          document.getElementById(`deck-slide-${to}`)?.focus();
        }}
      >
        {deck.slides.map((slide, index) => (
          <button
            key={index}
            id={`deck-slide-${index}`}
            role="tab"
            // A tab reports aria-selected, not aria-current, and names the
            // panel it drives — otherwise a screen reader announces eighteen
            // buttons with no relationship to the slide on the stage.
            aria-selected={index === shown}
            aria-controls="deck-stage-panel"
            tabIndex={index === shown ? 0 : -1}
            aria-label={uiText('Slide {value1}', { value1: index + 1 })}
            onClick={() => setAt(index)}
          >
            <DeckStage slide={slide} />
            <span className="number">{index + 1}</span>
          </button>
        ))}
      </div>
      {current.notes && (
        <div className="ws-deck-notes">
          <strong>{uiText('Speaker notes')}</strong>
          {current.notes}
        </div>
      )}
      {presenting && (
        <Presenter
          title={deck.title}
          slide={current}
          at={shown}
          count={count}
          onStep={step}
          onClose={stopPresenting}
        />
      )}
    </div>
  );
}
